import React, { useEffect, useState } from 'react';
import { Course, UserRole, Module } from '../../types/lms.types';
import AssignmentSection from '../Assignment/AssignmentSection';
import AnnouncementSection from '../Announcement/AnnouncementSection';
import GradesAnalytics from '../Analytics/GradesAnalytics';
import LiveClassMock from '../LiveClass/LiveClassMock';
import ModuleDropdown from './ModuleDropdown';
import AddModuleModal from './AddModuleModal';
import CreateContentDropdown from './CreateContentDropdown';
import ContentTabs from './ContentTabs';
import AllContentView from './AllContentView';
import AssessmentsView from './AssessmentsView';
import GlossaryView from './GlossaryView';
import OutcomesView from './OutcomesView';
import { getMockModules } from '../../services/lmsDataService';
import { MaterialsView } from './MaterialsView';
import { getAxiosTokenInstance } from '../../../utils/axiosInstance';
import { useQuery } from 'react-query';
import AddGroup from './AddGroup';
import ViewAssignmentSection from '../Assignment/ViewAssignmentSection';

interface CourseViewProps {
  course: any;
  userRole: UserRole;
  onBack: () => void;
 onViewAssignment?: (assignment: any, source?: string) => void;
}

const CourseView: React.FC<CourseViewProps> = ({ course, userRole, onBack, onViewAssignment }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showLiveClass, setShowLiveClass] = useState(false);
  const [courseData, setCourseData] = useState(course);
  const [selectedModule, setSelectedModule] = useState('all');
  const [modules, setModules] = useState<any[]>([]);  // Correctly type the modules state
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);

  // Fetch modules using the API
  useEffect(() => {
    const loadModules = async () => {
      const instance = await getAxiosTokenInstance();
      try {
        const resp = await instance.get('/college/lms/modules/getModules?collegeId=COLL-123&subjectId=subject123');
        if (resp?.data?.data) {
          setModules(resp.data.data); // Set fetched modules
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadModules();
  }, [course, userRole]);  // Only reload modules when course or userRole changes

  const handleModuleChange = (moduleId: string) => {
    setSelectedModule(moduleId); // Update selected module
  };

  const handleAddModule = (moduleData: Omit<Module, 'id'>) => {
    const newModule: Module = {
      id: `mod${modules.length + 1}`,  // Assign new id based on current modules length
      ...moduleData
    };
    // setModules((prevModules) => [...prevModules, newModule]);  // Add new module to state
  };

  const canCreateContent = userRole === 'teacher' || userRole === 'admin';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4">
          <button
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            ← Back to Dashboard
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">{courseData.name}</h1>
            <p className="text-gray-600 dark:text-gray-400">Instructor: {courseData.teachers}</p>
            <p className="text-sm text-gray-500 dark:text-gray-500">{courseData.count} students enrolled</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-3">
            {courseData.isLive && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="font-medium text-red-700">Live Class Ongoing</span>
                  </div>
                  <button
                    onClick={() => setShowLiveClass(true)}
                    className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    {userRole === 'teacher' ? 'Resume Class' : 'Join Now'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Module Selection Dropdown */}
      <ModuleDropdown
        modules={modules}
        selectedModule={selectedModule}
        onModuleChange={handleModuleChange}
        userRole={userRole}
        onAddModule={() => setShowAddModuleModal(true)}
      />

      {/* Enhanced Navigation Tabs */}
      <ContentTabs activeTab={activeTab} onTabChange={setActiveTab} userRole={userRole}/>

      {/* Create/Add Content Button */}
      {canCreateContent && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-end">
            <CreateContentDropdown
              selectedModule={selectedModule}
              modules={modules}
              subjectId={course.subjectId}
              onContentCreated={() => {
                console.log('Content created, refreshing...');
              }}
              students={course?.students}
            />
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 min-h-96">
        {activeTab === 'all' && (
          <AllContentView
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
            onViewAssignment={onViewAssignment}
          />
        )}
        {activeTab ==='add-group' && userRole === 'teacher' && (
          <AddGroup
          students={course?.students}
          subjectId={course.subjectId}
          modules={modules}
          

          
          />
        )

        }

        {activeTab === 'assessments' && (
          <AssessmentsView
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
            onViewAssignment={onViewAssignment}
            
          />
        )}

        {activeTab === 'materials' && (
          <MaterialsView
            course={course}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'announcements' && (
          <AnnouncementSection
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'glossary' && (
          <GlossaryView
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
            subjectId={course?.subjectId}
          />
        )}

        {activeTab === 'co' && (
          <OutcomesView
            type="course"
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
            
          />
        )}

        {activeTab === 'po' && (
          <OutcomesView
            type="program"
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'grades' && (
          <GradesAnalytics
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}
      </div>

      {/* Add Module Modal */}
      <AddModuleModal
        isOpen={showAddModuleModal}
        onClose={() => setShowAddModuleModal(false)}
        onAddModule={handleAddModule}
        allModules={course?.modules || modules}  // If no modules in course, use state `modules`
        subjectId={course?.subjectId}
      />

     
    </div>
  );
};

export default CourseView;
